import { TFunction } from 'i18next';

import { TRANSLATION_BASE_ID } from '$app-web/constants';
import { languages } from '$app-web/i18n';
import { ObjectType } from '$app-web/types';

import { hasObjectKeys, isObject, omitObjectKeys } from './object';

export function getTranslationTitle(translationKey: string, t: TFunction): string {
    return translationKey === TRANSLATION_BASE_ID
        ? t('rdash:base')
        : languages.get(translationKey).englishName || languages.get(translationKey).localName;
}

export function getTranslatedProperty<T extends ObjectType<string>, K extends keyof T & string>(
    data: T & { translations?: ObjectType },
    property: K,
    userLanguage: string,
    fallbackToBase = true
): string {
    if (userLanguage === TRANSLATION_BASE_ID) {
        return (data[property] as string) || '';
    }

    const translatableFieldName = `${property}Translation`;
    const translationsObject = (data.translations || data) as ObjectType;

    // Returns an empty string if the translation or base is falsy
    if (translationsObject != null && translationsObject[translatableFieldName] != null) {
        return (
            ((translationsObject[translatableFieldName] as ObjectType)[userLanguage] as string) ||
            (fallbackToBase && (data[property] as string)) ||
            ''
        );
    }
    return '';
}

export function getTranslatedObject(object: unknown, userLanguage: string, fallbackToBase = true): unknown {
    if (Array.isArray(object)) {
        return object.map((item) => getTranslatedObject(item, userLanguage, fallbackToBase));
    }

    if (isObject(object)) {
        const typedObject = object as ObjectType;
        if (hasObjectKeys(typedObject, 'translations')) {
            // it will first merges nested `translations` object (if any).
            return getTranslatedObject(
                {
                    ...(typedObject.translations as ObjectType),
                    ...omitObjectKeys(typedObject as Record<string, unknown>, ['translations']),
                },
                userLanguage,
                fallbackToBase
            );
        }
        return Object.keys(typedObject).reduce((previousValue, key) => {
            // The key was set before
            if (hasObjectKeys(previousValue as ObjectType, key)) {
                return previousValue;
            }
            // The key is translatable
            if (key.endsWith('Translation')) {
                const translatableKey = key.replace(/Translation$/, '');
                return {
                    ...previousValue,
                    [translatableKey]: getTranslatedProperty(
                        typedObject as ObjectType<string>,
                        translatableKey as keyof ObjectType<string> & string,
                        userLanguage,
                        fallbackToBase
                    ),
                };
            }
            // The key is not translatable but its value may have a translatable object
            return {
                ...previousValue,
                [key]: getTranslatedObject(typedObject[key], userLanguage, fallbackToBase),
            };
        }, {} as ObjectType);
    }

    return object;
}
